import React, { useMemo } from 'react'
import { router, usePage, useForm } from '@inertiajs/react'
import {
  Box, Button, TextField, Select, MenuItem, FormControl, InputLabel,
  Paper, Typography, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material'
import AppShell from '../../../../components/layout/AppShell'
import PageHeader from '../../../../components/ui/PageHeader'
import FormattedCurrency from '../../../../components/ui/FormattedCurrency'

Form.layout = (page) => <AppShell>{page}</AppShell>

export default function Form({ property, special_levy, resolutions = [], distribution_keys = [], action, errors: serverErrors = {} }) {
  const { props } = usePage()
  const tenant = props.tenant || {}
  const isEdit = action === 'edit'

  const { data, setData, post, put, processing, errors } = useForm({
    name: special_levy.name || '',
    amount: special_levy.amount || '',
    resolution_id: special_levy.resolution_id || '',
    distribution_key_id: special_levy.distribution_key_id || '',
    due_date: special_levy.due_date || '',
    installments: special_levy.installments || 1,
  })

  const allErrors = { ...serverErrors, ...errors }

  // Auto-suggest name from resolution
  const handleResolutionChange = (resolutionId) => {
    setData('resolution_id', resolutionId)
    if (!data.name) {
      const r = resolutions.find((r) => r.id === resolutionId)
      if (r) setData('name', r.title)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (isEdit) {
      put(`/m/${tenant.slug}/properties/${property.id}/special_levies/${special_levy.id}`)
    } else {
      post(`/m/${tenant.slug}/properties/${property.id}/special_levies`)
    }
  }

  return (
    <Box>
      <PageHeader
        title={isEdit ? 'Sonderumlage bearbeiten' : 'Neue Sonderumlage'}
        breadcrumbs={[
          { label: 'Sonderumlagen', href: `/m/${tenant.slug}/properties/${property.id}/special_levies` },
          { label: isEdit ? 'Bearbeiten' : 'Neue Sonderumlage' },
        ]}
      />

      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', maxWidth: 600 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth size="small" required error={!!allErrors.resolution_id}>
                <InputLabel>Beschluss</InputLabel>
                <Select value={data.resolution_id} label="Beschluss"
                  onChange={(e) => handleResolutionChange(e.target.value)}
                  data-testid="resolution-select">
                  <MenuItem value="">Bitte wählen</MenuItem>
                  {resolutions.map((r) => (
                    <MenuItem key={r.id} value={r.id}>
                      #{r.resolution_number} – {r.title} ({r.date})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Name" required fullWidth size="small"
                value={data.name} onChange={(e) => setData('name', e.target.value)}
                error={!!allErrors.name} helperText={allErrors.name?.[0]}
                slotProps={{ htmlInput: { 'data-testid': 'name-input' } }} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Gesamtbetrag (€)" required fullWidth size="small" type="number"
                value={data.amount} onChange={(e) => setData('amount', e.target.value)}
                error={!!allErrors.amount} helperText={allErrors.amount?.[0]}
                slotProps={{ htmlInput: { 'data-testid': 'amount-input', step: '0.01', min: 0 } }} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Fälligkeitsdatum" type="date" required fullWidth size="small"
                value={data.due_date} onChange={(e) => setData('due_date', e.target.value)}
                error={!!allErrors.due_date} helperText={allErrors.due_date?.[0]}
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': 'due-date-input' } }} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Raten" required fullWidth size="small" type="number"
                value={data.installments} onChange={(e) => setData('installments', e.target.value)}
                slotProps={{ htmlInput: { min: 1, step: 1 } }} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth size="small" required error={!!allErrors.distribution_key_id}>
                <InputLabel>Verteilerschlüssel</InputLabel>
                <Select value={data.distribution_key_id} label="Verteilerschlüssel"
                  onChange={(e) => setData('distribution_key_id', e.target.value)}
                  data-testid="distribution-key-select">
                  <MenuItem value="">Bitte wählen</MenuItem>
                  {distribution_keys.map((dk) => (
                    <MenuItem key={dk.id} value={dk.id}>{dk.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Preview info */}
          {data.amount && data.installments > 0 && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: 'info.lighter', borderRadius: 1 }}>
              <Typography variant="body2" fontWeight={500}>
                Gesamtbetrag: <FormattedCurrency value={data.amount} align="left" /> · {data.installments} Rate(n) à <FormattedCurrency value={parseFloat(data.amount) / parseInt(data.installments)} align="left" />
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button type="submit" variant="contained" disabled={processing} data-testid="submit-btn">
              {processing ? 'Wird gespeichert...' : 'Als Entwurf speichern'}
            </Button>
            <Button variant="outlined"
              onClick={() => router.visit(`/m/${tenant.slug}/properties/${property.id}/special_levies`)}>
              Abbrechen
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}
